import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { FileUploader } from '@/common/components/FileUploader'
import ImageWithDisplayName from '@/common/components/ImageWithDisplayName'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import ResumeBox from '@/common/components/ResumeBox'
import Spinner from '@/common/components/Spinner'
import Table from '@/common/components/Table'
import { cn } from '@/common/utils/cn'
import { formatDateTime } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { ModalCancelContractEnvelope } from '@/features/privateCompanyLeadsFunnel/components/ContractSignatureDetails/ClicksignSignature/ModalCancelContractEnvelope'
import { ModalResendContractSignatureEmails } from '@/features/privateCompanyLeadsFunnel/components/ContractSignatureDetails/ClicksignSignature/ModalResendContractSignatureEmails'
import { contractEnvelopeUserColumns } from '@/features/privateCompanyLeadsFunnel/constants/contractEnvelopeUserColumns.constant'
import { useUpdateContractEnvelopeSignatureType } from '@/features/privateCompanyLeadsFunnel/hooks/useUpdateContractEnvelopeSignatureType'
import { PrivateCompanyLeadContractEnvelopeTransformer } from '@/features/privateCompanyLeadsFunnel/services/privateCompanyLeadContractEnvelopeTransformer'
import { PrivateCompanyLeadData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead'
import { ContractEnvelopeData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLeadContractEnvelope'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircleIcon, CircleCheckIcon, FileBadgeIcon, UserRoundIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

interface ShowClicksignSignatureProps {
  privateCompanyLead: PrivateCompanyLeadData
  contractEnvelope?: ContractEnvelopeData
  contractEnvelopeIsLoading?: boolean
  disableFields?: boolean
}

export const ShowClicksignSignature = ({
  privateCompanyLead,
  contractEnvelope,
  contractEnvelopeIsLoading,
  disableFields,
}: ShowClicksignSignatureProps) => {
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLead.id] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLead.id] })
  }

  const { mutate: updateSignatureType, isPending: updateSignatureTypeIsPending } =
    useUpdateContractEnvelopeSignatureType({
      id: privateCompanyLead.id,
      onSuccess,
      onError: FormTransformers.errorsTransformer({ setMessages }),
    })

  useEffect(() => {
    if (!(contractEnvelope?.status === 'signed' && !privateCompanyLead?.contract?.file?.url)) {
      return
    }

    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: ['privateCompanyLead', privateCompanyLead.id],
      })
    }, 5000)

    return () => clearInterval(interval)
  }, [privateCompanyLead?.contract?.file?.url, contractEnvelope?.status, privateCompanyLead.id])

  return (
    <Box className="flex flex-col gap-y-2 p-4">
      <div className="flex items-start justify-between">
        <div className="">
          <p className="text-lg font-bold">Contrato (Assinatura sistema)</p>

          {contractEnvelope?.status && (
            <div className="flex items-center gap-x-2">
              {contractEnvelope?.status === 'signed' ? (
                <CircleCheckIcon className="size-6 text-success" />
              ) : contractEnvelope?.status === 'syncing' ? (
                <Spinner className="size-5 fill-white-400 text-white-200" />
              ) : (
                <AlertCircleIcon
                  strokeWidth={1}
                  className={cn('size-6 text-warning', {
                    'text-danger': [
                      'invalidated_by_tcard',
                      'remote_creation_failed',
                      'signed_document_download_failed',
                    ].includes(contractEnvelope.status),
                  })}
                />
              )}

              <p className="text-[11px] font-bold">
                {contractEnvelope?.status === 'syncing' &&
                  'O contrato está sendo sincronizado com o sistema de assinatura.'}

                {contractEnvelope?.status === 'awaiting_signatures' &&
                  'O contrato foi enviado mas ainda não foi assinado por todos.'}

                {['invalidated_by_tcard', 'remote_creation_failed', 'signed_document_download_failed'].includes(
                  contractEnvelope?.status,
                ) && 'Houve um problema com o contrato assinado.'}

                {contractEnvelope?.status === 'signed' &&
                  'O contrato já foi assinado mas ainda pode ser refeito, cancele e decline caso deseje reiniciar essa etapa.'}
              </p>
            </div>
          )}
        </div>

        {contractEnvelope?.status &&
        ['invalidated_by_tcard', 'remote_creation_failed', 'signed_document_download_failed'].includes(
          contractEnvelope?.status,
        ) ? (
          <Button
            type="button"
            onClick={() => updateSignatureType('manual')}
            className="flex items-center gap-x-2 bg-danger-op hover:bg-danger disabled:hover:bg-danger-op"
            disabled={updateSignatureTypeIsPending || disableFields}
            isLoading={updateSignatureTypeIsPending}
          >
            <p className="text-sm">Optar por assinatura manual</p>

            <FileBadgeIcon className="size-4" />
          </Button>
        ) : privateCompanyLead.state === 'quoting_finalization' ? (
          <ModalCancelContractEnvelope
            privateCompanyLeadId={privateCompanyLead.id}
            contractEnvelopeStatus={contractEnvelope?.status}
            setMessages={setMessages}
          />
        ) : null}
      </div>

      {privateCompanyLead?.contract?.file?.url && (
        <>
          <p className="text-sm font-bold">Contrato assinado</p>

          <div className="flex items-start gap-x-2">
            <FileUploader
              className="rounded-sm"
              fileName={getFilenameFromUrl(privateCompanyLead.contract?.file?.url)}
              currentFileUrl={privateCompanyLead.contract?.file?.url}
              disabled
            />

            <ResumeBox
              label="Recebimento do contrato assinado"
              value={
                privateCompanyLead?.contract?.file_attached_at
                  ? formatDateTime(new Date(privateCompanyLead?.contract?.file_attached_at))
                  : '--'
              }
              boxClassName="flex-1 bg-white-200 rounded-sm p-2"
              labelClassName="font-normal"
              valueClassName="font-bold"
              isLoading={contractEnvelopeIsLoading}
            />
          </div>
        </>
      )}

      <p className="text-sm font-bold">Minuta do contrato</p>

      <div className="flex items-start gap-x-2">
        <FileUploader
          className="rounded-sm"
          fileName={
            contractEnvelope?.file_to_sign?.url ? getFilenameFromUrl(contractEnvelope.file_to_sign?.url) : undefined
          }
          currentFileUrl={contractEnvelope?.file_to_sign?.url ? contractEnvelope.file_to_sign?.url : undefined}
          disabled
        />

        <div className="grid flex-1 grid-cols-2 gap-2">
          <ResumeBox
            label="Autor"
            value={
              <ImageWithDisplayName
                placeholderIcon={UserRoundIcon}
                imageUrl={contractEnvelope?.author?.profile_picture?.url || undefined}
                displayName={contractEnvelope?.author?.full_name || ''}
              />
            }
            boxClassName="col-span-2 bg-white-200 rounded-sm p-2"
            labelClassName="font-normal"
            isLoading={contractEnvelopeIsLoading}
          />

          <ResumeBox
            label="Enviado para assinatura"
            value={
              contractEnvelope?.sent_to_sign_at ? formatDateTime(new Date(contractEnvelope.sent_to_sign_at)) : '--'
            }
            boxClassName="bg-white-200 rounded-sm p-2"
            labelClassName="font-normal"
            valueClassName="font-bold"
            isLoading={contractEnvelopeIsLoading}
          />

          <ResumeBox
            label="Expira em"
            value={contractEnvelope?.deadline_at ? formatDateTime(new Date(contractEnvelope?.deadline_at)) : '--'}
            boxClassName="bg-white-200 rounded-sm p-2"
            labelClassName="font-normal"
            valueClassName="font-bold"
            isLoading={contractEnvelopeIsLoading}
          />
        </div>
      </div>

      <div className="flex items-start justify-between">
        <p className="text-sm font-bold">Assinaturas</p>

        {contractEnvelope?.status === 'awaiting_signatures' && (
          <ModalResendContractSignatureEmails privateCompanyLeadId={privateCompanyLead.id} setMessages={setMessages} />
        )}
      </div>

      <Table
        columns={contractEnvelopeUserColumns}
        rows={PrivateCompanyLeadContractEnvelopeTransformer.tableTransformer(contractEnvelope) || []}
        rowEmptyValue="--"
        page={1}
        totalPages={1}
        isLoadingData={false}
      />

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </Box>
  )
}
